"use client";

import React from "react";
import { ArrowUpRight, ChartBar as BarChart3, Bot, HeartPulse, Megaphone, Server } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
interface CategoryItem {
  id: string;
  title: string;
  tagline: string;
  resourceCount: string;
  focusArea: string;
  iconType: "marketing" | "ai" | "revops" | "healthcare" | "enterprise";
  to: string;
}
const CATEGORY_ITEMS: CategoryItem[] = [{
  id: "marketing",
  title: "Marketing & Demand Strategy",
  tagline: "Customer acquisition models, brand equity valuation, and attribution methodologies.",
  resourceCount: "48 publications",
  focusArea: "Growth & Retention",
  iconType: "marketing",
  to: "/IndustryInsightsPage"
}, {
  id: "ai-tech",
  title: "AI & Applied Systems",
  tagline: "Enterprise LLM integration, computational governance, and workflow automation audits.",
  resourceCount: "62 publications",
  focusArea: "Machine Intelligence",
  iconType: "ai",
  to: "/ResearchReportsPage"
}, {
  id: "revops",
  title: "Revenue Operations & Sales",
  tagline: "Pipeline velocity benchmarks, pricing architectures, and sales tech stack efficiency.",
  resourceCount: "35 publications",
  focusArea: "Go-to-Market",
  iconType: "revops",
  to: "/ResourcesPage"
}, {
  id: "healthcare",
  title: "Healthcare & Life Sciences",
  tagline: "Clinical data compliance, digital health adoption metrics, and regulatory impacts.",
  resourceCount: "29 publications",
  focusArea: "Healthtech & Bio",
  iconType: "healthcare",
  to: "/IndustryInsightsPage"
}, {
  id: "enterprise-cloud",
  title: "Enterprise Cloud & Security",
  tagline: "Infrastructure resilience, cyber risk allocation, and multi-cloud cost governance.",
  resourceCount: "54 publications",
  focusArea: "Cloud Infrastructure",
  iconType: "enterprise",
  to: "/ResearchReportsPage"
}];
function CategoryIcon({
  type
}: {
  type: CategoryItem["iconType"];
}) {
  const iconProps = {
    className: "size-5 text-foreground stroke-[1.5]"
  };
  switch (type) {
    case "marketing":
      return <Megaphone {...iconProps} />;
    case "ai":
      return <Bot {...iconProps} />;
    case "revops":
      return <BarChart3 {...iconProps} />;
    case "healthcare":
      return <HeartPulse {...iconProps} />;
    case "enterprise":
      return <Server {...iconProps} />;
    default:
      return <BarChart3 {...iconProps} />;
  }
}
export default function Categories() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1] as const
      }
    }
  };
  return <section id="industry-categories" data-nav="light" className="relative bg-background text-foreground py-20 sm:py-24 lg:py-28 border-b border-border selection:bg-muted selection:text-foreground">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead rule motif */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center justify-between gap-4 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <span className="h-2 w-2 rounded-full bg-foreground inline-block" />
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-sans">
                Research Index
              </p>
            </div>
            <p className="text-xs font-mono text-muted-foreground hidden sm:block">
              228 Active Industry Papers
            </p>
          </div>
          <div className="h-[0.5px] bg-border mt-0.5" />
        </div>

        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 sm:mb-16">
          <div className="max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15]">
              Explore research by industry sector
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-sans">
              Browse peer-reviewed whitepapers, market reports, and strategic executive guides across five core domains.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="rounded-sm border-border hover:bg-muted text-foreground px-5 py-2.5 font-medium text-sm transition-[background-color,border-color]">
              <Link to="/ResourcesPage" className="inline-flex items-center gap-2">
                <span>View full directory</span>
                <ArrowUpRight className="size-4 text-muted-foreground" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Editorial Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORY_ITEMS.map((item, i) => <motion.div key={item.id} data-index={i} variants={cardVariants} className={`group relative flex flex-col justify-between rounded-md border border-border bg-card p-6 sm:p-7 shadow-xs hover:shadow-md hover:border-foreground/30 transition-[border-color,box-shadow,background-color] duration-200 ${i === 4 ? "md:col-span-2 lg:col-span-1" : ""}`}>
              <div>
                {/* Header row with Icon, Badges & Link Indicator */}
                <div className="flex items-center justify-between gap-3 mb-6">
                  <div className="flex items-center justify-center size-10 rounded-sm bg-muted/60 border border-border group-hover:bg-muted transition-colors">
                    <CategoryIcon type={item.iconType} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="rounded-full bg-secondary text-secondary-foreground text-[11px] font-medium tracking-wide uppercase px-2.5 py-0.5">
                      {item.focusArea}
                    </Badge>
                  </div>
                </div>

                {/* Title & Description */}
                <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight group-hover:text-foreground transition-colors mb-3">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
                  {item.tagline}
                </p>
              </div>

              {/* Card Footer: Metadata & Navigation Link */}
              <div className="pt-4 border-t border-border/80 flex items-center justify-between mt-auto">
                <span className="text-xs font-mono text-muted-foreground">
                  {item.resourceCount}
                </span>

                <Link to={item.to} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-foreground group-hover:underline underline-offset-4" aria-label={`Browse ${item.title} publications`}>
                  <span>Explore</span>
                  <ArrowUpRight className="size-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                </Link>
              </div>
            </motion.div>)}

          {/* Research Request Card (6th Slot in the 3x2 Grid) */}
          <motion.div data-index={5} variants={cardVariants} className="flex flex-col justify-between rounded-md border border-dashed border-border bg-muted/30 p-6 sm:p-7 transition-[border-color,background-color] hover:bg-muted/50">
            <div>
              <div className="flex items-center justify-between gap-3 mb-6">
                <div className="flex items-center justify-center size-10 rounded-sm bg-background border border-border">
                  <span className="text-xs font-mono font-semibold text-muted-foreground">Q&A</span>
                </div>
                <Badge variant="outline" className="rounded-sm border-border bg-card text-muted-foreground text-[11px] font-mono px-2 py-0.5">
                  Custom Request
                </Badge>
              </div>

              <h3 className="font-serif text-xl sm:text-2xl font-semibold text-foreground tracking-tight mb-3">
                Need specialized sector analysis?
              </h3>
              <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-6">
                Our editorial research desk accepts commission inquiries and methodology review requests from enterprise leaders.
              </p>
            </div>

            <div className="pt-4 border-t border-border/80 flex items-center justify-between mt-auto">
              <span className="text-xs font-mono text-muted-foreground">
                B2B Advisory Desk
              </span>
              <Button asChild variant="ghost" size="sm" className="h-auto p-0 font-semibold uppercase tracking-wider text-xs text-foreground hover:bg-transparent hover:underline underline-offset-4">
                <Link to="/AboutUsPage">
                  <span>Contact Editors</span>
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}